/**
 * Registro comandi dell'assistente vocale del TIME Watch — Fase 1.
 *
 * Filosofia: modulare ed estendibile.
 * Ogni comando è un oggetto con:
 *  - id: identificatore univoco
 *  - patterns: array di frasi normalizzate che, se trovate nel testo dopo la
 *              wake phrase "Ora TIME", attivano il comando (il match più lungo vince)
 *  - action: 'navigate' | 'pmv-page'
 *  - target:  path route (per navigate) oppure indice di pagina 0..6 (per pmv-page)
 *  - label:   etichetta italiana mostrata all'utente come feedback
 *
 * Nuovi comandi (Timer vocali, Sveglie vocali, Agenti AI, ecc.) potranno essere
 * aggiunti semplicemente estendendo l'array COMMANDS o aggiungendo nuovi
 * "action" (es. 'timer-start', 'reminder-create'…).
 */

/**
 * Normalizza una stringa: minuscolo, senza accenti, senza punteggiatura,
 * spazi collassati. Serve a rendere il match indipendente da varianti.
 */
export function normalize(s) {
  return String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Wake phrase ufficiale: "Ora TIME".
 * Includiamo alcune tolleranze note del riconoscimento vocale italiano che
 * spesso trascrive "TIME" come parola inglese (time/taim/team). NON si attiva
 * mai con "Ora" o "TIME" pronunciate da sole.
 */
export const WAKE_PATTERNS = ["ora time", "ora taim", "ora team", "hora time"];

/**
 * Elenco comandi supportati dalla Fase 1.
 * L'ordine dei pattern è indifferente: il matcher sceglie sempre il match
 * più lungo per evitare collisioni (es. "modalita time watch" vince su "time").
 */
export const COMMANDS = [
  // ── Cambio modalità ────────────────────────────────────────────────
  {
    id: "mode-time-watch",
    patterns: [
      "passa alla modalita time watch",
      "modalita time watch",
      "vai alla modalita time watch",
    ],
    action: "navigate",
    target: "/innovazione/time-watch/pmv",
    label: "Modalità TIME Watch",
  },
  {
    id: "mode-utility",
    patterns: [
      "passa alla modalita utility",
      "modalita utility",
      "vai alla modalita utility",
    ],
    action: "navigate",
    target: "/innovazione/time-watch/utility",
    label: "Modalità Utility",
  },
  {
    id: "mode-orologio",
    patterns: [
      "passa alla modalita orologio",
      "modalita orologio",
      "vai alla modalita orologio",
    ],
    action: "navigate",
    target: "/innovazione/time-watch/orologio",
    label: "Modalità Orologio",
  },

  // ── TIME Watch (pagine PMV, carosello 0..6) ────────────────────────
  {
    id: "pmv-opportunita",
    patterns: [
      "apri opportunita",
      "opportunita",
      "apri tempo per lasciare il segno",
      "tempo per lasciare il segno",
    ],
    action: "pmv-page",
    target: 0,
    label: "Opportunità",
  },
  {
    id: "pmv-oggi",
    patterns: [
      "apri oggi",
      "oggi",
      "apri tempo a disposizione oggi",
      "tempo a disposizione oggi",
    ],
    action: "pmv-page",
    target: 1,
    label: "Oggi",
  },
  {
    id: "pmv-denaro",
    patterns: [
      "apri denaro",
      "denaro",
      "apri patrimonio",
      "patrimonio",
    ],
    action: "pmv-page",
    target: 2,
    label: "Denaro",
  },
  {
    id: "pmv-liberta",
    patterns: [
      "apri liberta",
      "liberta",
      "apri tempo libero",
      "tempo libero",
    ],
    action: "pmv-page",
    target: 3,
    label: "Libertà",
  },
  {
    id: "pmv-mitel",
    patterns: [
      "apri mitel",
      "mitel",
      "apri portafoglio mitel",
      "portafoglio mitel",
      "apri banconote universali",
      "banconote universali",
    ],
    action: "pmv-page",
    target: 4,
    label: "MITEL",
  },
  {
    id: "pmv-offerta",
    patterns: [
      "apri offerta",
      "offerta",
      "apri offerto",
      "offerto",
      "apri tempo dato",
      "tempo dato",
    ],
    action: "pmv-page",
    target: 5,
    label: "Offerta",
  },
  {
    id: "pmv-ricompensa",
    patterns: [
      "apri ricompensa",
      "ricompensa",
      "apri riconosciuto",
      "riconosciuto",
      "apri tempo avuto",
      "tempo avuto",
    ],
    action: "pmv-page",
    target: 6,
    label: "Ricompensa",
  },

  // ── Utility ────────────────────────────────────────────────────────
  {
    id: "utility-timer",
    patterns: ["apri timer", "timer"],
    action: "navigate",
    target: "/innovazione/time-watch/utility/timer",
    label: "Timer",
  },
  {
    id: "utility-promemoria",
    patterns: ["apri promemoria", "promemoria"],
    action: "navigate",
    target: "/innovazione/time-watch/utility/promemoria",
    label: "Promemoria",
  },
  {
    id: "utility-cronometro",
    patterns: ["apri cronometro", "cronometro"],
    action: "navigate",
    target: "/innovazione/time-watch/utility/cronometro",
    label: "Cronometro",
  },
  {
    id: "utility-sveglia",
    patterns: ["apri sveglia", "sveglia"],
    action: "navigate",
    target: "/innovazione/time-watch/utility/sveglia",
    label: "Sveglia",
  },
  {
    id: "utility-impronta",
    patterns: ["apri impronta", "impronta"],
    action: "navigate",
    target: "/innovazione/time-watch/utility/impronta",
    label: "Impronta",
  },
  {
    id: "utility-istanti",
    patterns: ["apri istante", "istante", "apri istanti", "istanti"],
    action: "navigate",
    target: "/innovazione/time-watch/utility/istanti",
    label: "Istante",
  },

  // ── Orologio ───────────────────────────────────────────────────────
  {
    id: "orologio-open",
    patterns: ["apri orologio", "orologio"],
    action: "navigate",
    target: "/innovazione/time-watch/orologio",
    label: "Orologio",
  },

  // ── Aiuto contestuale ──────────────────────────────────────────────
  // Non naviga: mostra un piccolo toast temporaneo con l'elenco delle
  // modalità disponibili. Gestito dal componente OraTimeButton.
  {
    id: "help",
    patterns: ["aiuto", "cosa puoi fare"],
    action: "help",
    target: null,
    label: "Aiuto",
  },
];

/**
 * Cerca la wake phrase nella trascrizione normalizzata. Ritorna l'indice
 * (in caratteri) subito successivo alla wake phrase, oppure -1 se assente.
 */
function findWakeEnd(norm) {
  let best = -1;
  for (const wp of WAKE_PATTERNS) {
    const idx = norm.lastIndexOf(wp);
    if (idx >= 0) {
      const end = idx + wp.length;
      if (end > best) best = end;
    }
  }
  return best;
}

/**
 * Interpreta una trascrizione vocale.
 * Ritorna: { matched: Command|null, hasWake: boolean, rest: string }
 * - matched: comando riconosciuto (o null)
 * - hasWake: true se è stata pronunciata la wake phrase
 * - rest:    porzione di testo dopo la wake phrase (utile per debug/futuro)
 */
export function matchCommand(transcript) {
  const norm = normalize(transcript);
  const wakeEnd = findWakeEnd(norm);
  if (wakeEnd < 0) return { matched: null, hasWake: false, rest: norm };

  const rest = norm.slice(wakeEnd).trim();
  if (!rest) return { matched: null, hasWake: true, rest };

  // Match del pattern più lungo (word-boundary via padding con spazi).
  const paddedRest = ` ${rest} `;
  let best = null;
  let bestLen = 0;
  for (const cmd of COMMANDS) {
    for (const p of cmd.patterns) {
      const paddedPat = ` ${p} `;
      if (paddedRest.includes(paddedPat) && p.length > bestLen) {
        best = cmd;
        bestLen = p.length;
      }
    }
  }
  return { matched: best, hasWake: true, rest };
}
